import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import api, { API, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, UsersRound, Loader2 } from "lucide-react";

export default function Join() {
  const { token } = useParams();
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    axios.get(`${API}/workspaces/invites/${token}`).then(({ data }) => setInvite(data)).catch((e) => setError(e?.response?.data?.detail || "Invite not found"));
  }, [token]);

  const accept = async () => {
    setJoining(true);
    try {
      const { data } = await api.post(`/workspaces/invites/${token}/accept`);
      await refresh();
      localStorage.removeItem("op_pending_join");
      nav("/team", { replace: true, state: { joined: data.name } });
    } catch (e) {
      setError(formatError(e));
    } finally {
      setJoining(false);
    }
  };

  const goAuth = (path) => {
    localStorage.setItem("op_pending_join", token);
    nav(path);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6" data-testid="join-page">
      <Card className="w-full max-w-md p-8 space-y-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Sparkles className="h-4 w-4" /></div>
          <div className="font-display font-extrabold">OutlierPulse</div>
        </div>
        {error ? (
          <div className="text-sm text-muted-foreground" data-testid="join-error">{error}</div>
        ) : !invite ? (
          <div className="text-muted-foreground text-sm">Loading invite…</div>
        ) : (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Team invite</div>
              <h1 className="font-display text-2xl font-extrabold mt-1 leading-snug" data-testid="join-title">
                {invite.invited_by_name || "A teammate"} invited you to <span className="text-primary">{invite.workspace_name}</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5"><UsersRound className="h-4 w-4" /> {invite.member_count} member{invite.member_count !== 1 ? "s" : ""} · invite sent to {invite.email} · joining as <span className="font-semibold text-foreground capitalize">{invite.role || "editor"}</span></p>
              {invite.status === "accepted" && <p className="text-xs text-[hsl(var(--amber))] mt-2" data-testid="join-status-hint">This invite was already used — ask the workspace owner for a new link if you still need access.</p>}
              {invite.status === "expired" && <p className="text-xs text-[hsl(var(--rose))] mt-2" data-testid="join-status-hint">This invite link has expired — ask the workspace owner for a new one.</p>}
              {user && invite.status === "pending" && user.email?.toLowerCase() !== invite.email?.toLowerCase() && (
                <p className="text-xs text-[hsl(var(--rose))] mt-2" data-testid="join-email-mismatch">You're signed in as {user.email}, but this invite is for {invite.email}. Sign in with that account to join.</p>
              )}
            </div>
            {loading ? null : user ? (
              <div className="space-y-2">
                <Button className="w-full" onClick={accept} disabled={joining || invite.status !== "pending" || user.email?.toLowerCase() !== invite.email?.toLowerCase()} data-testid="join-accept-button">
                  {joining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Join as {user.name}
                </Button>
                <Link to="/" className="block text-center text-xs text-muted-foreground hover:underline">Not now</Link>
              </div>
            ) : (
              <div className="space-y-2">
                <Button className="w-full" onClick={() => goAuth("/register")} data-testid="join-register-button">Create account & join</Button>
                <Button variant="outline" className="w-full" onClick={() => goAuth("/login")} data-testid="join-login-button">I already have an account</Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
